import hasErrorsMixin from '@/common/mixins/hasErrorsMixin'
import moment from 'moment'
import { OrganizationSubjects } from '@/enums/OrganizationSubjects'
import { ApiClient } from '@/helpers/api-client/ApiClient'
import { DaData } from '../helpers/api-client/DaData'
import { Logger } from '../helpers/Logger'
import { ApplicationAccessLevels } from '../enums/ApplicationAccessLevels'
import { PassKinds } from '@/common/enums/PassKinds'
import { Typeaheads } from '../helpers/api-client/Typeaheads'
import { DaDataDistricts } from '@/common/enums/DaDataDistricts'
import { PersonalIdentifications } from '@/common/enums/PersonalIdentifications'

export default {
  mixins: [
    hasErrorsMixin
  ],

  computed: {
    disabledInput() {
      return Boolean(this.isView)
    },

    isCurrentContractFieldsDisplay() {
      return Number(this.configuration.need_contract) === 1 || Number(this.configuration.need_contract) === 2
    },

    canImportVisitorsFromExcel() {
      return Number(this.configuration.can_import_excel) === 1
    },

    isView() {
      return this.checkAccessLevel(ApplicationAccessLevels.View)
    },

    isEdit() {
      return this.checkAccessLevel(ApplicationAccessLevels.Edit)
    },

    isDuplicate() {
      return this.checkAccessLevel(ApplicationAccessLevels.Duplicate)
    },

    isDraft() {
      return this.checkAccessLevel(ApplicationAccessLevels.Draft)
    },

    isNew() {
      return !this.isView && !this.isEdit && !this.isDuplicate && !this.isDraft
    },

    needAgentCheck() {
      return Number(this.form.application.need_agent_check) === 1
    },

    isEntityOrganizationSubject() {
      return this.form.organization.subject_id === OrganizationSubjects.Entity
    },

    isFizOrganizationSubject() {
      return this.form.organization.subject_id === OrganizationSubjects.Fiz
    },

    isIndividualOrganizationSubject() {
      return this.form.organization.subject_id === OrganizationSubjects.Individual
    },

    isGkoOrganizationSubject() {
      return this.form.organization.subject_id === OrganizationSubjects.Gko
    },

    isTerminalSubject() {
      return this.form.organization.subject_id === OrganizationSubjects.Terminal
    },

    isForeignSubject() {
      return this.form.organization.subject_id === OrganizationSubjects.Foreign
    },

    isPassKindPedestrian() {
      return (this.form.application?.pass_kind === PassKinds.PEDESTRIAN)
    },

    isPassKindMaterial() {
      return (this.form.application?.pass_kind === PassKinds.MATERIAL)
    },

    isPassKindTransport() {
      return (this.form.application?.pass_kind === PassKinds.TRANSPORT)
    },

    isPassKindUniversal() {
      return (this.form.application?.pass_kind === PassKinds.UNIVERSAL)
    },

    isPassKindNull() {
      return (this.form.application?.pass_kind === null)
    },

    isOrganizationResidencyRus() {
      return (this.nationalitiesList.find((n) => n.id === this.form.organization.nationality_id)).iso2 === 'RU'
    }
  },

  methods: {
    checkAccessLevel(accessLevel) {
      return Boolean(this.$route.query.application_id && this.$route.query.access_level === accessLevel)
    },

    disableSubmitButton() {
      Logger.log('Disabling submit button...')
      this.$emit('disable-submit-button')
    },

    enableSubmitButton() {
      Logger.log('Enabling submit button...')
      this.$emit('enable-submit-button')
    },

    resetTypeaheadActiveIndex(typeaheadRef) {
      this.$nextTick(() => {
        if (!typeaheadRef) {
          return
        }

        if (typeaheadRef && Array.isArray(typeaheadRef)) {
          for (const ref in typeaheadRef) {
            if (typeaheadRef[ref]) {
              typeaheadRef[ref].activeIndex = 0
            }
          }
        } else {
          typeaheadRef.activeIndex = 0
        }
      })
    },

    async updateFIO(query, done) {
      let parts = ''
      const dbPersonalsTypeahead = []
      let fioList = []

      if (document.activeElement.id.includes('organization')) {
        parts = null
      } else if (document.activeElement.id.includes('lastname')) {
        parts = 'SURNAME'
      } else if (document.activeElement.id.includes('firstname')) {
        parts = 'NAME'
      } else if (document.activeElement.id.includes('name')) {
        parts = 'PATRONYMIC'
      }

      if (parts === 'SURNAME') {
        const typeaheads = await Typeaheads.personal(query)
        this.typeaheads.personals = typeaheads && typeaheads.personals ? typeaheads.personals : []

        for (let i = 0; i < this.typeaheads?.personals?.length; i++) {
          dbPersonalsTypeahead[i] = {
            value: this.typeaheads.personals[i].search,
            data: this.typeaheads.personals[i]
          }
          dbPersonalsTypeahead[i].data.value = dbPersonalsTypeahead[i].data.search
        }

        let count = 0
        for (let i = 0; i < dbPersonalsTypeahead.length; i++) {
          if (dbPersonalsTypeahead[i].value && dbPersonalsTypeahead[i].value.indexOf(query) === 0) {
            fioList[count] = dbPersonalsTypeahead[i]
            count++
          }
        }
      }

      let data = {
        parts: [parts]
      }

      if (
        this.visitorPersonal.fullname_last !== '' &&
        ['NAME', 'PATRONYMIC'].includes(parts) &&
        ['MALE', 'FEMALE'].includes(this.visitorPersonal?.gender)
      ) {
        data.gender = this.visitorPersonal.gender
      }

      const fioDaData = await DaData.suggestFio(query, data)

      fioList = fioList.concat(fioDaData.suggestions.map(item => ({ dadata: true, ...item })))

      done(fioList)
    },

    async typeaheadOrganizationName(
      query,
      done,
      organizationNationalityId = null,
      organizationSubjectId = null,
      isGroupBlock = true
    ) {
      if (this.isForeignSubject) {
        return
      }

      if (this.isFizOrganizationSubject) {
        await this.updateFIO(query, done)

        return
      }

      let nationalityId = this.form.organization.nationality_id
      if (organizationNationalityId !== null) {
        nationalityId = organizationNationalityId
      }

      if (nationalityId === 182) {
        done(await this.getOrganizationSuggestions(query, isGroupBlock, false, organizationSubjectId))
      }
      if (nationalityId === 113) {
        done(await this.getKzOrganizationSuggestions(query, isGroupBlock, false, organizationSubjectId))
      }
      if (nationalityId === 20) {
        done(await this.getBelorusOrganizationSuggestions(query, isGroupBlock, false, organizationSubjectId))
      }
    },

    async typeaheadOrganizationInn(query, done, isGroupBlock = true) {
      if (this.form.organization.nationality_id === 182) {
        done(await this.getOrganizationSuggestions(query, isGroupBlock, true))
      }
      if (this.form.organization.nationality_id === 113) {
        done(await this.getKzOrganizationSuggestions(query, isGroupBlock, true))
      }
      if (this.form.organization.nationality_id === 20) {
        done(await this.getBelorusOrganizationSuggestions(query, isGroupBlock, true))
      }
    },

    async getOrganizationSuggestions(
      query,
      isGroupBlock = true,
      inn = false,
      organizationSubjectId = null
    ) {
      let subjectId = this.form.organization.subject_id
      if (organizationSubjectId !== null) {
        subjectId = organizationSubjectId
      }

      let type = 'LEGAL'
      if (subjectId === OrganizationSubjects.Individual) {
        type = 'INDIVIDUAL'
      }
      this.locations = DaDataDistricts.RUS[this.locationName]

      const organizationsRequest = await DaData.suggestParty(query, {
        type,
        locations: isGroupBlock ? this.locations : [],
        residency: 'RU'
      })

      if (!organizationsRequest.suggestions || !Array.isArray(organizationsRequest.suggestions)) {
        return
      }

      return this.getDaDataSuggestions(organizationsRequest, inn)
    },

    async getKzOrganizationSuggestions(
      query,
      isGroupBlock = true,
      inn = false,
      organizationSubjectId = null
    ) {
      let subjectId = this.form.organization.subject_id
      if (organizationSubjectId !== null) {
        subjectId = organizationSubjectId
      }

      if (subjectId === OrganizationSubjects.Individual) {
        return
      }
      const organizationsRequest = await DaData.suggestParty(query, {
        residency: 'KZ'
      })

      if (!organizationsRequest.suggestions || !Array.isArray(organizationsRequest.suggestions)) {
        return
      }

      return this.getDaDataSuggestions(organizationsRequest, inn)
    },

    async getBelorusOrganizationSuggestions(
      query,
      isGroupBlock = true,
      inn = false,
      organizationSubjectId = null
    ) {
      let subjectId = this.form.organization.subject_id
      if (organizationSubjectId !== null) {
        subjectId = organizationSubjectId
      }

      let type = 'ORGANIZATION'
      if (subjectId === OrganizationSubjects.Individual) {
        type = 'INDIVIDUAL'
      }
      const organizationsRequest = await DaData.suggestParty(query, {
        type,
        residency: 'BY'
      })

      if (!organizationsRequest.suggestions || !Array.isArray(organizationsRequest.suggestions)) {
        return
      }

      return this.getDaDataSuggestions(organizationsRequest, inn)
    },

    async typeaheadContractOrgName(query, done) {
      const organizationsRequest = await DaData.suggestParty(query, {
        type: 'LEGAL',
        locations: [],
        residency: 'RU'
      })

      if (!organizationsRequest.suggestions || !Array.isArray(organizationsRequest.suggestions)) {
        return
      }

      done(await this.getDaDataSuggestions(organizationsRequest, false))
    },

    async getDaDataSuggestions(organizationsRequest, inn) {
      const suggestions = organizationsRequest.suggestions.map(item => {
        if (inn) {
          item.value = item.data ? item.data.inn : null
        }

        return item
      })

      return suggestions
    },

    async suggestAddresses(query, onlyCity = false, entity) {
      const data = {}
      data.nationalityId = entity === 'organization' ? this.form.organization.nationality_id : 182
      if (onlyCity) {
        data.from_bound = {
          value: 'city'
        }

        data.to_bound = {
          value: 'city'
        }
      }

      return DaData.suggestAddress(query, data)
    },

    async typeaheadAddresses(query, done, onlyCity, entity) {
      const addresses = await this.suggestAddresses(query, onlyCity, entity)

      if (!addresses.suggestions || !Array.isArray(addresses.suggestions)) {
        return
      }

      if (Number(this.configuration.show_full_address) === 1) {
        addresses.suggestions = addresses.suggestions.map(address => {
          const getFullValue = function (valueWithType, type, typeFull, value) {
            return valueWithType === type + ' ' + value || valueWithType === typeFull + ' ' + value
              ? typeFull + ' ' + value
              : value + ' ' + typeFull
          }
          let data = address.data

          address.value = [
            [null, data.city_with_type].includes(data.region_with_type) === false
              ? getFullValue(data.region_with_type, data.region_type, data.region_type_full, data.region)
              : null,
            data.area_with_type !== null
              ? getFullValue(data.area_with_type, data.area_type, data.area_type_full, data.area)
              : null,
            data.city_with_type !== null
              ? getFullValue(data.city_with_type, data.city_type, data.city_type_full, data.city)
              : null,
            data.settlement_with_type !== null
              ? getFullValue(data.settlement_with_type, data.settlement_type, data.settlement_type_full, data.settlement)
              : null,
            data.street_with_type !== null
              ? getFullValue(data.street_with_type, data.street_type, data.street_type_full, data.street)
              : null,
            data.stead !== null ? data.stead_type_full + ' ' + data.stead : null,
            data.house !== null ? data.house_type_full + ' ' + data.house : null,
            data.block !== null ? data.block_type_full + ' ' + data.block : null,
            data.flat !== null ? data.flat_type_full + ' ' + data.flat : null,
            data.room !== null ? data.room_type_full + ' ' + data.room : null
          ].filter(currentValue => currentValue !== null).join(', ')

          return address
        })
      }
      done(addresses.suggestions)
    },

    checkVisitorOrganization(visitor) {
      const visitorOrganizationName = visitor.personal.organization_name
      const appOrganizationName = this.form.organization.name

      visitor.personal.visitorOrganizationErr = !!(visitorOrganizationName === appOrganizationName && appOrganizationName && visitorOrganizationName)
      if (visitor.personal.visitorOrganizationErr) {
        this.disableSubmitButton()
      } else {
        this.enableSubmitButton()
      }
    },

    checkInnControlSum(visitor = null) {
      const inn = visitor !== null ? visitor.personal.organization_inn : this.form.organization.innData
      let isErr = false
      if (inn) {
        const innMain = inn.substr(0).split('')

        if (this.form.organization.nationality_id === 182) {
          if (Number(inn.length) === 10) {
            const controlSum = ((2 * Number(innMain[0]) + 4 * Number(innMain[1]) + 10 * Number(innMain[2]) +
              3 * Number(innMain[3]) + 5 * Number(innMain[4]) + 9 * Number(innMain[5]) + 4 * Number(innMain[6]) +
              6 * Number(innMain[7]) + 8 * Number(innMain[8])) % 11) % 10

            isErr = Number(controlSum) !== Number(inn.substr(-1, 1))
          } else if (Number(inn.length) === 12) {
            const controlSum1 = ((7 * Number(innMain[0]) + 2 * Number(innMain[1]) + 4 * Number(innMain[2]) +
              10 * Number(innMain[3]) + 3 * Number(innMain[4]) + 5 * Number(innMain[5]) + 9 * Number(innMain[6]) +
              4 * Number(innMain[7]) + 6 * Number(innMain[8]) + 8 * Number(innMain[9])) % 11) % 10

            const controlSum2 = ((3 * Number(innMain[0]) + 7 * Number(innMain[1]) + 2 * Number(innMain[2]) +
              4 * Number(innMain[3]) + 10 * Number(innMain[4]) + 3 * Number(innMain[5]) + 5 * Number(innMain[6]) +
              9 * Number(innMain[7]) + 4 * Number(innMain[8]) + 6 * Number(innMain[9]) + 8 * Number(innMain[10])) % 11) % 10

            isErr = Number(controlSum1) !== Number(inn.substr(-2, 1)) && Number(controlSum2) !== Number(inn.substr(-1, 1))
          } else {
            isErr = true
          }
        }
      }

      if (visitor !== null) {
        visitor.personal.innControlSumErr = isErr
      } else {
        this.form.organization.innControlSumErr = isErr
      }

      if (this.isPassKindTransport) {
        let visitor = this.form.visitors[0]
        for (let i = 0; i < visitor.transports; i++) {
          for (let j = 0; j < visitor.transports[i].drivers.length; j++) {
            const err = visitor.transports[i].drivers[j].personal.innControlSumErr || this.form.organization.innControlSumErr
            if (err) {
              this.innControlSumErr = err
              break
            } else {
              this.innControlSumErr = false
            }
          }
        }
      } else {
        for (let i = 0; i < this.form.visitors.length; i++) {
          const err = this.form.visitors[i].personal.innControlSumErr || this.form.organization.innControlSumErr
          if (err) {
            this.innControlSumErr = err
            break
          } else {
            this.innControlSumErr = false
          }
        }
      }

    },

    async checkVisitorsInPass(visitorKey) {
      if (this.form.visitors[visitorKey].personal.identification_number) {
        await ApiClient
          .checkCurrentVisitorApplications({
            visitor: this.form.visitors[visitorKey].personal,
            application: this.form.application
          })
          .then(response => {
            if (response.data && response.data.message) {
              this.form.visitors[visitorKey].personal.visitorErr = response.data.message
            } else {
              this.form.visitors[visitorKey].personal.visitorErr = null
            }
          })
      } else {
        this.form.visitors[visitorKey].personal.visitorErr = null
      }
    },

    dateOptionConfigurator() {
      let date = moment('1799-12-31')

      if (!this.isView) {
        date = moment().add(-1, 'days')
      }

      return date.toDate()
    },

    russianCivilPassportSelected(visitor) {
      return visitor.personal.identification_id === PersonalIdentifications.PASSPORT &&
        visitor.personal.nationality_id === 182
    },

    checkPassportSeriesIsCorrect(yearInSeries) {
      const currentYear = (new Date()).getFullYear()
      let minYear = 1997
      let arr = []
      for (let i = minYear; i < currentYear + 4; i++) {
        arr.push(i.toString().substring(2))
      }

      return arr.indexOf(yearInSeries) === -1
    },

    checkPassportSeriesIsAccordToDate(yearInSeries, identificationDateOf) {
      const yearOfStart = (moment(identificationDateOf, 'DD.MM.YYYY')).year()
      let maxYear = yearOfStart + 3
      let minYear = yearOfStart - 3
      let arr = []
      for (let i = minYear; i < maxYear + 1; i++) {
        arr.push(i.toString().substring(2))
      }

      return arr.indexOf(yearInSeries) === -1
    },

    fieldVisible(fieldAlias, passSection) {
      let visible = false
      if (this.cfgFormFields[fieldAlias]) {
        visible = this.cfgFormFields[fieldAlias].pass_kind_visible.includes(this.form.application.pass_kind) &&
          this.cfgFormFields[fieldAlias].section === passSection
      }

      return Boolean(visible)
    },

    fieldWidth(fieldAlias) {
      return this.cfgFormFields[fieldAlias] ? this.cfgFormFields[fieldAlias].width * 4 : 4
    },

    fieldTitle(fieldAlias) {
      let title
      if (this.cfgFormFields[fieldAlias] && this.cfgFormFields[fieldAlias].title) {
        title = this.cfgFormFields[fieldAlias].title
      }

      return title
    },

    fieldHint(fieldAlias) {
      let hint
      if (this.cfgFormFields[fieldAlias] && this.cfgFormFields[fieldAlias].hint) {
        hint = this.cfgFormFields[fieldAlias].hint
      }

      return hint
    }
  }
}
